import { type ReactNode, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Wallet, FileText, LineChart, BookOpen, LogOut, User, Layers, Eye, ArrowLeft } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { logout, apiFetch, getViewAsInvestor, setViewAsInvestor, type AuthUser } from "@/lib/api";
import { useSession } from "@/lib/session";

const NAV = [
  { to: "/investidor/posicoes", label: "Minhas posições", icon: Wallet },
  { to: "/investidor/fidcs", label: "Meus FIDCs", icon: Layers },
  { to: "/investidor/distribuicoes", label: "Distribuições", icon: FileText },
  { to: "/investidor/nav", label: "Histórico NAV", icon: LineChart },
  { to: "/investidor/lamina", label: "Lâmina ANBIMA", icon: BookOpen },
  { to: "/investidor/perfil", label: "Meu perfil", icon: User },
];

type Investor = { id: string; full_name: string; email: string; n_grants: number };

// Barra de seleção que aparece SÓ para o admin: escolhe qual cotista visualizar. O UUID escolhido
// é persistido (getViewAsInvestor/setViewAsInvestor) e o apiFetch o envia como x-view-as-investor;
// o backend reescreve a identidade nas rotas /investor/*, então o portal inteiro passa a mostrar os
// dados daquele investidor. Trocar o investidor remonta o conteúdo (key) → refetch de tudo.
function AdminViewAsBar({ value, onChange }: { value: string | null; onChange: (id: string | null) => void }) {
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    apiFetch<{ investors: Investor[] }>("/admin/investors")
      .then((r) => { if (alive) setInvestors(r.investors ?? []); })
      .catch(() => { if (alive) setInvestors([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);
  return (
    <div className="flex items-center gap-3 flex-wrap px-4 lg:px-8 py-2.5 bg-amber-500/10 border-b border-amber-500/30">
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
        <Eye className="h-3.5 w-3.5" /> Modo administrador · visualizar como
      </span>
      <select
        className="h-8 min-w-[220px] rounded-md border border-input bg-background px-2 text-sm"
        value={value ?? ""}
        disabled={loading}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">{loading ? "Carregando investidores…" : "— selecione um investidor —"}</option>
        {investors.map((i) => (
          <option key={i.id} value={i.id}>
            {i.full_name} · {i.n_grants} FIDC{i.n_grants === 1 ? "" : "s"}
          </option>
        ))}
      </select>
      <span className="text-[11px] text-muted-foreground">somente leitura</span>
    </div>
  );
}

export function InvestorShell({ user, children }: { user: AuthUser; children: ReactNode }) {
  const navigate = useNavigate();
  const { setUser } = useSession();
  const isAdmin = user.role === "admin";
  const [target, setTarget] = useState<string | null>(isAdmin ? getViewAsInvestor() : null);

  function handleTargetChange(id: string | null) {
    setViewAsInvestor(id);
    setTarget(id);
  }

  async function handleLogout() {
    await logout();
    setUser(null);
    navigate("/login", { replace: true });
  }

  return (
    <div className="h-screen overflow-hidden flex bg-muted/40">
      <aside className="hidden lg:flex h-screen w-60 shrink-0 flex-col border-r border-border bg-background overflow-hidden">
        <div className="h-16 flex items-center px-5 border-b border-border shrink-0">
          <Logo />
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-0.5">
          <div className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
            {isAdmin ? "Visualização (admin)" : "Portal do Cotista"}
          </div>
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                  isActive ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:bg-muted",
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-4 space-y-2">
          <div className="text-xs leading-tight">
            <div className="font-semibold truncate">{user.full_name}</div>
            <div className="text-muted-foreground truncate">{user.email}</div>
          </div>
          {isAdmin ? (
            <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/app")}>
              <ArrowLeft className="h-4 w-4" /> Voltar ao admin
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="w-full" onClick={handleLogout}>
              <LogOut className="h-4 w-4" /> Sair
            </Button>
          )}
        </div>
      </aside>
      <main className="flex-1 min-w-0 min-h-0 h-screen overflow-y-auto">
        <header className="h-14 border-b border-border bg-background flex items-center px-4 lg:px-8 justify-between sticky top-0 z-30">
          <div className="lg:hidden"><Logo /></div>
          <div className="hidden lg:block text-sm text-muted-foreground">
            {isAdmin ? "Visualização do Portal do Investidor — admin" : "Portal do Cotista — CredCapital"}
          </div>
          <Button variant="ghost" size="sm" onClick={isAdmin ? () => navigate("/app") : handleLogout} aria-label={isAdmin ? "Voltar" : "Sair"} className="lg:hidden">
            {isAdmin ? <ArrowLeft className="h-4 w-4" /> : <LogOut className="h-4 w-4" />}
          </Button>
        </header>
        {isAdmin && <AdminViewAsBar value={target} onChange={handleTargetChange} />}
        <div className="px-4 lg:px-10 py-6 lg:py-8 max-w-6xl mx-auto min-w-0">
          {isAdmin && !target ? (
            <div className="rounded-lg border border-dashed border-border bg-background p-10 text-center">
              <Eye className="h-8 w-8 mx-auto mb-3 text-muted-foreground/60" />
              <div className="font-semibold">Selecione um investidor</div>
              <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                Use o seletor acima para visualizar o Portal do Investidor exatamente como o cotista
                escolhido o vê ao acessar o sistema (posições, FIDCs concedidos, distribuições, NAV e lâmina).
              </p>
            </div>
          ) : (
            // key por investidor: trocar o alvo remonta as telas → refetch com o novo header
            <div key={target ?? "self"}>{children}</div>
          )}
        </div>
      </main>
    </div>
  );
}
